import { useState } from "react";
import { CalendarRange, Loader2, Repeat } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip } from "@/components/common/Chrome";
import { DateField } from "@/components/common/DateField";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BLANK = { space_code: "", title: "", booked_for: "", booking_type: "external",
  days_of_week: [], start_date: "", end_date: "", start_time: "18:00", end_time: "20:00",
  attendees: 0, contact: "", notes: "", exclude_dates: [], skip_closures: true,
  skip_conflicts: false };

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);

/** Weekly series booking: the whole run is checked for clashes in one pass before anything books. */
export const RecurringBooking = ({ spaces, onBooked }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const set = (key, value) => { setForm({ ...form, [key]: value }); setPreview(null); };
  const toggleDay = (index) => set("days_of_week", form.days_of_week.includes(index)
    ? form.days_of_week.filter((d) => d !== index) : [...form.days_of_week, index]);
  const toggleExclude = (day) => setPreview({
    ...preview,
    excluded: preview.excluded?.includes(day)
      ? preview.excluded.filter((d) => d !== day) : [...(preview.excluded || []), day],
  });

  const ready = form.space_code && form.title.trim().length > 2 && form.start_date && form.end_date;

  const run = async (path, extra = {}) => {
    setBusy(path);
    try {
      const res = await api.post(`/property/bookings/recurring${path}`, { ...form, ...extra });
      setBusy("");
      return res.data;
    } catch (e) { toast.error(apiError(e)); setBusy(""); return null; }
  };

  const check = async () => {
    const data = await run("/preview");
    if (data) setPreview({ ...data, excluded: [] });
  };

  const book = async () => {
    const data = await run("", {
      skip_conflicts: true,
      exclude_dates: [...form.exclude_dates, ...(preview.excluded || [])],
    });
    if (!data) return;
    toast.success(`${data.occurrences} bookings created as ${data.series_ref}`);
    setOpen(false);
    setForm(BLANK);
    setPreview(null);
    await onBooked();
  };

  const clashes = (preview?.occurrences || []).filter((o) => o.status === "clash");
  const bookable = (preview?.occurrences || []).filter(
    (o) => o.status === "available" && !(preview.excluded || []).includes(o.date));

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPreview(null); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="recurring-booking-button"
                className="h-8 rounded-full border-border px-3 text-[11px]">
          <Repeat className="mr-1.5 h-3.5 w-3.5 text-primary" /> Recurring booking
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto border-border bg-card"
                     data-testid="recurring-dialog">
        <DialogHeader>
          <DialogTitle>Book a repeating slot</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Set the pattern, check the whole run for clashes in one pass, then book the dates that
            are free.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <F label="Space">
              <Select value={form.space_code} onValueChange={(v) => set("space_code", v)}>
                <SelectTrigger data-testid="recurring-space-select" className="bg-muted/40">
                  <SelectValue placeholder="Which room or hall?" />
                </SelectTrigger>
                <SelectContent>
                  {spaces.map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.name}{s.room_number ? ` (${s.room_number})` : ""} · seats {s.capacity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
          </div>
          <F label="Purpose">
            <Input value={form.title} data-testid="recurring-title-input"
                   onChange={(e) => set("title", e.target.value)} className="bg-muted/40" />
          </F>
          <F label="Booked for">
            <Input value={form.booked_for} data-testid="recurring-for-input"
                   onChange={(e) => set("booked_for", e.target.value)} className="bg-muted/40" />
          </F>
          <F label="Type">
            <Select value={form.booking_type} onValueChange={(v) => set("booking_type", v)}>
              <SelectTrigger data-testid="recurring-type-select" className="bg-muted/40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="external">External hire (chargeable)</SelectItem>
                <SelectItem value="internal">Internal use (no charge)</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Expected attendance">
            <Input type="number" value={form.attendees} data-testid="recurring-attendees-input"
                   onChange={(e) => set("attendees", Number(e.target.value))}
                   className="bg-muted/40 font-mono" />
          </F>
          <F label="First date">
            <DateField value={form.start_date} testId="recurring-start-date"
                       onChange={(v) => set("start_date", v)} clearable={false} />
          </F>
          <F label="Last date">
            <DateField value={form.end_date} testId="recurring-end-date"
                       onChange={(v) => set("end_date", v)} clearable={false} />
          </F>
          <F label="From">
            <Input type="time" value={form.start_time} data-testid="recurring-start-time"
                   onChange={(e) => set("start_time", e.target.value)}
                   className="bg-muted/40 font-mono" />
          </F>
          <F label="Until">
            <Input type="time" value={form.end_time} data-testid="recurring-end-time"
                   onChange={(e) => set("end_time", e.target.value)}
                   className="bg-muted/40 font-mono" />
          </F>
          <div className="sm:col-span-2">
            <F label="Which days of the week"
               hint="Leave empty to repeat weekly on the same weekday as the first date.">
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d, i) => (
                  <button key={d} type="button" onClick={() => toggleDay(i)}
                          data-testid={`recurring-day-${d.toLowerCase()}`}
                          className={`rounded-full border px-3 py-1.5 text-[11.5px] transition-colors duration-150 ${
                            form.days_of_week.includes(i)
                              ? "border-primary bg-primary/15 text-primary"
                              : "border-border bg-muted/40 text-muted-foreground hover:text-foreground"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </F>
          </div>
          <div className="sm:col-span-2">
            <F label="Notes">
              <Textarea rows={2} value={form.notes} data-testid="recurring-notes-input"
                        onChange={(e) => set("notes", e.target.value)}
                        className="bg-muted/40 text-[12px]" />
            </F>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/25 p-3 sm:col-span-2">
            <div className="text-[12px] text-foreground">
              Skip organisation closure dates
              <p className="text-[11px] text-muted-foreground">
                Dates recorded as closures are left out of the series.
              </p>
            </div>
            <Switch checked={form.skip_closures} data-testid="recurring-closures-switch"
                    onCheckedChange={(v) => set("skip_closures", v)} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={check} disabled={!ready || busy === "/preview"}
                  data-testid="recurring-check-button"
                  className="h-9 rounded-full bg-primary px-4 text-[12px] text-primary-foreground hover:bg-primary/90">
            {busy === "/preview" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <CalendarRange className="mr-1.5 h-3.5 w-3.5" />} Check the whole run
          </Button>
          {preview && (
            <>
              <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    data-testid="recurring-available-chip">
                {bookable.length} free
              </Chip>
              {clashes.length > 0 && (
                <Chip className="border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                      data-testid="recurring-clash-chip">
                  {clashes.length} clash
                </Chip>
              )}
              {preview.summary.skipped > 0 && (
                <Chip className="border-border bg-muted text-muted-foreground">
                  {preview.summary.skipped} closure dates skipped
                </Chip>
              )}
              <Chip className="border-border bg-muted text-muted-foreground">
                {money(preview.summary.charge_each, preview.summary.currency)} each ·{" "}
                {money(bookable.length * preview.summary.charge_each, preview.summary.currency)} total
              </Chip>
            </>
          )}
        </div>

        {preview && (
          <div className="max-h-[280px] overflow-y-auto rounded-xl border border-border"
               data-testid="recurring-preview">
            <table className="w-full border-collapse text-[11.5px]">
              <tbody>
                {preview.occurrences.map((o) => {
                  const excluded = (preview.excluded || []).includes(o.date);
                  return (
                    <tr key={o.date} className="border-b border-border last:border-0"
                        data-testid={`recurring-row-${o.date}`}>
                      <td className="px-3 py-1.5 font-mono text-[11px] text-foreground">
                        {o.date} · {o.starts_at.slice(11, 16)}–{o.ends_at.slice(11, 16)}
                      </td>
                      <td className="px-3 py-1.5">
                        {o.status === "available" && !excluded && (
                          <span className="text-emerald-600 dark:text-emerald-400">Free</span>
                        )}
                        {excluded && <span className="text-muted-foreground">Excluded</span>}
                        {o.status === "clash" && (
                          <span className="text-red-600 dark:text-red-400">
                            Clash — {o.reason}
                            {o.clash_ref ? ` (${o.clash_ref})` : ""}
                          </span>
                        )}
                        {o.status === "skipped" && (
                          <span className="text-muted-foreground">{o.reason}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {o.status === "available" && (
                          <button type="button" onClick={() => toggleExclude(o.date)}
                                  data-testid={`recurring-exclude-${o.date}`}
                                  className="text-[11px] text-muted-foreground hover:text-foreground">
                            {excluded ? "Include" : "Exclude"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {preview && (
          <Button onClick={book} disabled={busy === "" && bookable.length === 0}
                  data-testid="recurring-book-button"
                  className="h-10 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy === "" && false}
            Book the {bookable.length} free date{bookable.length === 1 ? "" : "s"}
            {clashes.length > 0 ? ` and skip the ${clashes.length} clash${clashes.length === 1 ? "" : "es"}` : ""}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
